import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMember } from "@/context/MemberAuthContext";
import { api } from "@/lib/api";
import { memberApi } from "@/lib/memberApi";
import ServiceDetails from "@/pages/member/ServiceDetails";
import { PageShell, PageHero } from "@/components/layout/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast, Toaster } from "sonner";
import { CheckCircle2, AlertCircle, FileText, LogOut, CreditCard, User, Upload, BookOpen, CalendarClock, BellRing } from "lucide-react";
import { DateField } from "@/components/common/DateField";
import { formatDate } from "@/lib/dateFormat";

const SOCIALS = ["facebook", "instagram", "youtube", "x", "linkedin", "website"];
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");

const MemberDashboard = () => {
  const { member, logout, refresh } = useMember();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [grs, setGrs] = useState([]);
  const [letters, setLetters] = useState([]);
  const [unreadLetters, setUnreadLetters] = useState(0);
  const [reqs, setReqs] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [payCfg, setPayCfg] = useState({ enabled: false });
  const [selected, setSelected] = useState({});
  const [paying, setPaying] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (member === false) navigate("/member/login"); }, [member, navigate]);
  useEffect(() => { if (member) setForm({ ...member, social: member.social || {}, visibility: member.visibility || {} }); }, [member]);
  useEffect(() => {
    memberApi.grs().then((d) => setGrs(d.slice(0, 6))).catch(() => {});
    memberApi.letters().then((d) => { setLetters(d.letters || []); setUnreadLetters(d.unread || 0); }).catch(() => {});
    memberApi.paymentRequests().then(setReqs).catch(() => {});
    memberApi.receipts().then(setReceipts).catch(() => {});
    memberApi.paymentsConfig().then(setPayCfg).catch(() => {});
  }, []);

  const pendingReqs = reqs.filter((r) => r.status !== "paid");
  useEffect(() => {
    setSelected((prev) => {
      const next = {};
      pendingReqs.forEach((r) => { next[r.id] = r.id in prev ? prev[r.id] : true; });
      return next;
    });
  }, [reqs]);

  const selectedReqs = pendingReqs.filter((r) => selected[r.id]);
  const selectedTotal = selectedReqs.reduce((s, r) => s + Number(r.amount), 0);

  const openMemberGr = async (g) => {
    try {
      const res = await memberApi.openGr(g.id);
      if (res.file_url) window.open(mediaUrl(res.file_url), "_blank");
      else toast.error("No file attached to this document");
      setGrs((prev) => prev.map((x) => (x.id === g.id ? { ...x, is_new: false, opened: true } : x)));
    } catch { toast.error("Could not open the document"); }
  };

  const downloadMemberGr = async (g) => {
    try {
      const res = await memberApi.downloadGr(g.id);
      if (res.file_url) window.open(mediaUrl(res.file_url), "_blank");
      else toast.error("No file to download");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download not allowed"); }
  };

  const openLetter = async (lt) => {
    try {
      const blob = await memberApi.letterFileBlob(lt.letter_id);
      window.open(URL.createObjectURL(blob), "_blank");
      if (!lt.read_at) {
        await memberApi.markLetterRead(lt.delivery_id);
        setLetters((prev) => prev.map((x) => x.delivery_id === lt.delivery_id ? { ...x, read_at: new Date().toISOString() } : x));
        setUnreadLetters((n) => Math.max(0, n - 1));
      }
    } catch { toast.error("Could not open the letter"); }
  };

  const loadPayments = () => {
    memberApi.paymentRequests().then(setReqs);
    memberApi.receipts().then(setReceipts);
  };

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const pay = async (r) => {
    if (!payCfg.enabled) { toast.message("Online payment activates once the association's Razorpay test keys are added."); return; }
    try {
      const order = await memberApi.createOrder(r.id);
      await loadRazorpay();
      const rz = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: "INR", order_id: order.order_id,
        name: "GMOAA", description: r.name_en,
        handler: async (resp) => {
          try {
            const v = await memberApi.verifyPayment({ razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
            toast.success(`Paid! Receipt ${v.receipt_number}`); loadPayments();
          } catch { toast.error("Could not verify payment"); }
        },
      });
      rz.open();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); }
  };

  const payCombined = async () => {
    if (!payCfg.enabled) { toast.message("Online payment activates once the association's Razorpay test keys are added."); return; }
    const ids = selectedReqs.map((r) => r.id);
    if (ids.length < 2) { toast.error("Select at least two fees to pay together"); return; }
    setPaying(true);
    try {
      const order = await memberApi.createCombinedOrder(ids);
      await loadRazorpay();
      const rz = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: "INR", order_id: order.order_id,
        name: "GMOAA", description: `${ids.length} fees`,
        handler: async (resp) => {
          try {
            const v = await memberApi.verifyCombinedPayment({ razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature });
            toast.success(`Paid! ${(v.receipt_numbers || []).length} receipts issued`); loadPayments();
          } catch { toast.error("Could not verify payment"); }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rz.open();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); }
    finally { setPaying(false); }
  };

  if (!member || !form) return <PageShell><div className="gmoaa-container py-20 text-center text-gmoaa-text-muted">Loading…</div></PageShell>;

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setSocial = (k) => (e) => setForm((f) => ({ ...f, social: { ...f.social, [k]: e.target.value } }));
  const setVis = (k) => (v) => setForm((f) => ({ ...f, visibility: { ...f.visibility, [k]: v } }));

  const save = async () => {
    try {
      await memberApi.updateProfile({
        photo_url: form.photo_url, bio: form.bio, qualification: form.qualification,
        registration_no: form.registration_no, reg_end_date: form.reg_end_date, email: form.email, social: form.social, visibility: form.visibility,
      });
      await refresh();
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) { toast.error("Only PNG or JPG images are allowed"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be 2 MB or smaller"); return; }
    setUploading(true);
    try {
      const res = await memberApi.uploadPhoto(file);
      setForm((f) => ({ ...f, photo_url: res.photo_url }));
      await refresh();
      toast.success("Photo updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const doLogout = () => { logout(); navigate("/"); };

  const fields = [form.photo_url, form.bio, form.qualification, form.registration_no, form.email];
  const filled = fields.filter(Boolean).length;
  const progress = Math.round((filled / fields.length) * 100);

  return (
    <PageShell>
      <Toaster position="top-center" richColors />
      <PageHero title={`Welcome, ${member.name_en}`} subtitle={member.name_gu} />
      <div className="gmoaa-container grid grid-cols-1 gap-6 py-10 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* Status */}
          <div className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-4">
            <div className="flex items-center gap-3">
              {member.status === "green"
                ? <span className="flex items-center gap-1.5 rounded-full bg-gmoaa-green-light px-3 py-1 text-sm font-medium text-gmoaa-green"><CheckCircle2 size={16} /> Verified member</span>
                : <span className="flex items-center gap-1.5 rounded-full bg-gmoaa-saffron-light px-3 py-1 text-sm font-medium text-gmoaa-saffron-hover"><AlertCircle size={16} /> Not yet verified</span>}
              <span className="text-xs text-gmoaa-text-muted">ID: {member.member_id}</span>
              {member.reg_end_date && <span className="flex items-center gap-1 rounded-full bg-gmoaa-saffron-light px-2.5 py-1 text-xs font-medium text-gmoaa-saffron-hover" data-testid="member-reg-end-chip"><CalendarClock size={13} /> Reg. valid till {formatDate(member.reg_end_date)}</span>}
            </div>
            <Button variant="ghost" size="sm" className="text-gmoaa-red" onClick={doLogout}><LogOut size={15} className="mr-1" /> Logout</Button>
          </div>

          {/* User guide */}
          <a href={memberApi.guideUrl()} target="_blank" rel="noopener noreferrer" data-testid="member-guide-btn"
             className="flex items-center justify-between rounded-xl border border-gmoaa-green/30 bg-gmoaa-green-light/40 p-4 transition-colors hover:bg-gmoaa-green-light">
            <div className="flex items-center gap-3">
              <BookOpen size={20} className="text-gmoaa-green" />
              <div>
                <p className="text-sm font-semibold text-gmoaa-green">Member User Guide (PDF)</p>
                <p className="text-xs text-gmoaa-text-muted">How to use every feature · English &amp; ગુજરાતી</p>
              </div>
            </div>
            <FileText size={16} className="text-gmoaa-saffron-hover" />
          </a>

          {/* Complete profile */}
          <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-5" data-testid="member-profile-card">
            <div className="mb-4 flex items-center gap-2">
              <User size={18} className="text-gmoaa-green" />
              <h3 className="font-serif text-lg font-semibold text-gmoaa-green">Complete your profile</h3>
            </div>
            <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-gmoaa-green-light">
              <div className="h-full rounded-full bg-gmoaa-green transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="mb-4 flex items-center gap-4">
              <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-full border-2 border-gmoaa-green-light bg-gmoaa-surface">
                {form.photo_url
                  ? <img src={mediaUrl(form.photo_url)} alt="" className="h-full w-full object-cover" data-testid="member-avatar" />
                  : <div className="flex h-full w-full items-center justify-center text-gmoaa-text-muted"><User size={28} /></div>}
              </div>
              <div>
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-3 py-1.5 text-sm font-medium text-gmoaa-green transition-colors hover:bg-gmoaa-green-light">
                  <Upload size={14} /> {uploading ? "Uploading…" : form.photo_url ? "Change photo" : "Upload photo"}
                  <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onPhoto} disabled={uploading} data-testid="member-photo-input" />
                </label>
                <p className="mt-1 text-xs text-gmoaa-text-muted">PNG or JPG · up to 2 MB</p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><Label className="mb-1 block text-xs text-gmoaa-text-muted">Email</Label><Input data-testid="member-email" value={form.email || ""} onChange={set("email")} className="border-gmoaa-border-subtle" /></div>
              <div><Label className="mb-1 block text-xs text-gmoaa-text-muted">Qualification</Label><Input value={form.qualification || ""} onChange={set("qualification")} className="border-gmoaa-border-subtle" placeholder="BAMS, MD (Ayu)" /></div>
              <div><Label className="mb-1 block text-xs text-gmoaa-text-muted">Medical registration no.</Label><Input value={form.registration_no || ""} onChange={set("registration_no")} className="border-gmoaa-border-subtle" /></div>
              <div>
                <Label className="mb-1 flex items-center gap-1 text-xs text-gmoaa-text-muted"><CalendarClock size={12} /> Registration end date</Label>
                <DateField value={form.reg_end_date || ""} onChange={set("reg_end_date")} testid="member-reg-end-date" />
              </div>
              <div className="sm:col-span-2"><Label className="mb-1 block text-xs text-gmoaa-text-muted">Short bio</Label><Textarea rows={3} value={form.bio || ""} onChange={set("bio")} className="border-gmoaa-border-subtle" /></div>
            </div>

            <h4 className="mb-2 mt-5 text-sm font-semibold text-gmoaa-text-dark">My social links</h4>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {SOCIALS.map((s) => (
                <div key={s}><Label className="mb-1 block text-xs capitalize text-gmoaa-text-muted">{s}</Label><Input value={form.social?.[s] || ""} onChange={setSocial(s)} className="border-gmoaa-border-subtle" placeholder="https://..." /></div>
              ))}
            </div>

            <h4 className="mb-2 mt-5 text-sm font-semibold text-gmoaa-text-dark">What others can see</h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[["bio", "Short bio"], ["email", "Email"], ["social", "Social links"]].map(([k, label]) => (
                <div key={k}>
                  <Label className="mb-1 block text-xs text-gmoaa-text-muted">{label}</Label>
                  <Select value={form.visibility?.[k] || "members"} onValueChange={setVis(k)}>
                    <SelectTrigger className="border-gmoaa-border-subtle"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Everyone</SelectItem>
                      <SelectItem value="members">Members only</SelectItem>
                      <SelectItem value="self_hidden">Only me</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <Button data-testid="member-save-profile" onClick={save} className="mt-5 bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save profile</Button>
          </div>
          <ServiceDetails />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-5">
            <div className="mb-3 flex items-center gap-2"><CreditCard size={18} className="text-gmoaa-saffron" /><h3 className="font-serif text-lg font-semibold text-gmoaa-green">This year's fees &amp; payments</h3></div>
            {reqs.length === 0 && receipts.length === 0 && <p className="rounded-lg bg-gmoaa-surface p-3 text-sm text-gmoaa-text-muted">Nothing is due right now.</p>}
            <div className="space-y-2" data-testid="member-payments">
              {reqs.map((r) => (
                <div key={r.id} className="rounded-lg border border-gmoaa-border-subtle p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {r.status !== "paid" && pendingReqs.length >= 2 && (
                        <Checkbox
                          data-testid={`fee-check-${r.id}`}
                          checked={!!selected[r.id]}
                          onCheckedChange={(v) => setSelected((s) => ({ ...s, [r.id]: !!v }))}
                          className="border-gmoaa-border-subtle data-[state=checked]:bg-gmoaa-green data-[state=checked]:border-gmoaa-green"
                        />
                      )}
                      <p className="text-sm font-medium text-gmoaa-text-dark">{r.name_en}{r.fy ? <span className="ml-1 text-xs font-normal text-gmoaa-text-muted">· {r.fy}</span> : null}</p>
                    </div>
                    <span className="font-serif font-bold text-gmoaa-green">₹{Number(r.amount).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    {r.status === "paid"
                      ? <span className="rounded-full bg-gmoaa-green-light px-2 py-0.5 text-xs font-semibold text-gmoaa-green">Paid</span>
                      : <>
                          <span className="rounded-full bg-gmoaa-saffron-light px-2 py-0.5 text-xs font-semibold text-gmoaa-saffron-hover">Pending{r.due_date ? ` · due ${formatDate(r.due_date)}` : ""}</span>
                          <Button size="sm" data-testid={`pay-btn-${r.id}`} onClick={() => pay(r)} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Pay now</Button>
                        </>}
                  </div>
                </div>
              ))}
            </div>
            {pendingReqs.length >= 2 && (
              <div className="mt-3 rounded-lg border border-gmoaa-green/30 bg-gmoaa-green-light/40 p-3" data-testid="combined-pay-box">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gmoaa-text-dark">Selected total ({selectedReqs.length})</span>
                  <span data-testid="combined-total" className="font-serif text-lg font-bold text-gmoaa-green">₹{selectedTotal.toLocaleString("en-IN")}</span>
                </div>
                <Button
                  data-testid="pay-selected-btn"
                  disabled={paying || selectedReqs.length < 2}
                  onClick={payCombined}
                  className="mt-2 w-full bg-gmoaa-saffron text-white hover:bg-gmoaa-saffron-hover disabled:opacity-50"
                >
                  <CreditCard size={15} className="mr-1.5" /> {paying ? "Opening…" : "Pay selected together"}
                </Button>
              </div>
            )}
            {receipts.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gmoaa-text-muted">My receipts</p>
                <div className="space-y-1.5">
                  {receipts.map((rc) => (
                    <button key={rc.id} onClick={() => navigate(`/member/receipt/${rc.id}`)} className="flex w-full items-center justify-between rounded-lg border border-gmoaa-border-subtle px-3 py-2 text-left text-sm hover:bg-gmoaa-green-light/50">
                      <span className="truncate text-gmoaa-text-dark">{rc.purpose_en}</span>
                      <span className="shrink-0 text-xs text-gmoaa-saffron-hover">{rc.number} ›</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-5" data-testid="member-notices-card">
            <div className="mb-3 flex items-center gap-2">
              <BellRing size={18} className="text-gmoaa-green" />
              <h3 className="font-serif text-lg font-semibold text-gmoaa-green">Notices / Letters</h3>
              {unreadLetters > 0 && <span data-testid="letters-unread-badge" className="rounded-full bg-gmoaa-red px-2 py-0.5 text-[10px] font-semibold text-white">{unreadLetters} new</span>}
            </div>
            <div className="space-y-2">
              {letters.map((lt) => (
                <div key={lt.delivery_id} data-testid={`member-letter-${lt.delivery_id}`} className="rounded-lg border border-gmoaa-border-subtle p-2.5">
                  <p className="line-clamp-2 text-sm font-medium text-gmoaa-text-dark">{!lt.read_at && <span className="mr-1 inline-block h-2 w-2 rounded-full bg-gmoaa-red align-middle" />}{lt.subject}</p>
                  <p className="text-xs text-gmoaa-text-muted">{lt.ref_no}{lt.date ? ` · ${lt.date}` : ""}</p>
                  <button data-testid={`member-letter-open-${lt.delivery_id}`} onClick={() => openLetter(lt)} className="mt-1.5 flex items-center gap-1 rounded-md border border-gmoaa-border-subtle px-2 py-1 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light"><BookOpen size={13} /> View &amp; download</button>
                </div>
              ))}
              {letters.length === 0 && <p className="text-xs text-gmoaa-text-muted">No letters received yet.</p>}
            </div>
          </div>
          <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-5">
            <div className="mb-3 flex items-center gap-2"><FileText size={18} className="text-gmoaa-green" /><h3 className="font-serif text-lg font-semibold text-gmoaa-green">New for you</h3></div>
            <div className="space-y-2">
              {grs.map((g) => (
                <div key={g.id} data-testid={`member-gr-${g.id}`} className="rounded-lg border border-gmoaa-border-subtle p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium text-gmoaa-text-dark">{g.title_en}{g.is_new && <span className="ml-1.5 rounded bg-gmoaa-red-bg px-1 py-0.5 text-[9px] font-semibold text-gmoaa-red">NEW</span>}</p>
                      <p className="text-xs text-gmoaa-text-muted">{g.number} · {formatDate(g.date)}{g.visibility === "members" ? " · Members only" : ""}</p>
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button data-testid={`member-gr-view-${g.id}`} onClick={() => openMemberGr(g)}
                      className="flex items-center gap-1 rounded-md border border-gmoaa-border-subtle px-2 py-1 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light"><BookOpen size={13} /> View</button>
                    {g.allow_download !== false && (
                      <button data-testid={`member-gr-download-${g.id}`} onClick={() => downloadMemberGr(g)}
                        className="flex items-center gap-1 rounded-md border border-gmoaa-border-subtle px-2 py-1 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light"><FileText size={13} /> PDF</button>
                    )}
                  </div>
                </div>
              ))}
              {grs.length === 0 && <p className="text-xs text-gmoaa-text-muted">No documents yet.</p>}
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default MemberDashboard;
